import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Maximize2, Loader2, MapPin, Clock, Truck, CheckCircle } from 'lucide-react';
import { apiRequest, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function DriverLogbookWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogbook();
  }, []);

  const fetchLogbook = async () => {
    try {
      const result = await apiRequest('/driver/dashboard');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch logbook');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'start_warehouse': return <Truck className="h-3 w-3 text-blue-500" />;
      case 'reached_store': return <MapPin className="h-3 w-3 text-green-500" />;
      case 'left_store': return <CheckCircle className="h-3 w-3 text-purple-500" />;
      case 'break_start': return <Clock className="h-3 w-3 text-amber-500" />;
      case 'break_end': return <Clock className="h-3 w-3 text-amber-500" />;
      case 'end_day': return <CheckCircle className="h-3 w-3 text-slate-500" />;
      default: return <Clock className="h-3 w-3 text-slate-400" />;
    }
  };

  const formatAction = (action) => {
    const labels = {
      'start_warehouse': 'Started from Warehouse',
      'reached_store': 'Reached Store',
      'left_store': 'Left Store',
      'break_start': 'Break Started',
      'break_end': 'Break Ended',
      'end_day': 'Day Completed'
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  return (
    <Card data-testid="driver-logbook-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-500" />
            <CardTitle className="text-lg">My Logbook</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/driver/dashboard')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : !data ? (
          <div className="text-center py-4 text-slate-400">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No logbook data</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Today's Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-lg font-bold text-blue-700">{data.today_stats?.trips_today || 0}</p>
                <p className="text-xs text-blue-600">Trips</p>
              </div>
              <div className="p-2 bg-green-50 rounded">
                <p className="text-lg font-bold text-green-700">{data.today_stats?.stores_visited || 0}</p>
                <p className="text-xs text-green-600">Stores</p>
              </div>
              <div className="p-2 bg-purple-50 rounded">
                <p className="text-lg font-bold text-purple-700">{data.today_stats?.deliveries_completed || 0}</p>
                <p className="text-xs text-purple-600">Deliveries</p>
              </div>
            </div>

            {/* Recent Activity */}
            {data.logbook && data.logbook.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-slate-500">Recent Activity</p>
                {data.logbook.slice(0, 3).map((entry, idx) => (
                  <div key={entry.id || idx} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded text-xs">
                    {getActionIcon(entry.action)}
                    <span className="flex-1">{formatAction(entry.action)}</span>
                    {entry.store_name && (
                      <Badge variant="secondary" className="text-xs py-0">
                        {entry.store_name}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Current Status */}
            {data.current_status && (
              <div className="p-2 bg-indigo-50 rounded text-center">
                <p className="text-xs text-indigo-600">Current Status</p>
                <p className="font-medium text-indigo-700">{data.current_status}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
